import React from 'react';

/**
 * The app's icon set, inlined so every glyph ships with the bundle and can be
 * recoloured with `currentColor`. Sizes default to the tab-bar size (21px);
 * pass `size` where a context needs larger.
 */

interface IconProps {
  size?: number;
  className?: string;
}

/** Dotted trail to an X — "My crawl". Dashes (not dots) so the marks survive
 *  21px, and the X is a heavier stroke than the trail so it anchors the icon. */
export const TrailIcon: React.FC<IconProps> = ({ size = 21, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <circle cx="4.6" cy="19.4" r="1.6" fill="currentColor" />
    <path
      d="M5.2 18.4C8.4 17.4 6.6 13.6 9.6 12.1 12.4 10.7 12.8 8.6 15.4 7"
      strokeWidth="2"
      strokeDasharray="2.6 2.4"
    />
    <path d="M16.8 3.9l4 4M20.8 3.9l-4 4" strokeWidth="2.7" />
  </svg>
);

/** Bulleted list — "All crawls". */
export const ListIcon: React.FC<IconProps> = ({ size = 21, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M9 6h11M9 12h11M9 18h11" />
    <path d="M4.5 6h.01M4.5 12h.01M4.5 18h.01" />
  </svg>
);

/** Bar chart — "Leaderboard". */
export const ChartIcon: React.FC<IconProps> = ({ size = 21, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M7 21V11M12 21V4M17 21v-6" />
  </svg>
);

/** Circled i — the About link in the app bar. */
export const InfoIcon: React.FC<IconProps> = ({ size = 21, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    className={className}
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 16.5v-5M12 8h.01" />
  </svg>
);

/** Paper plane — the directions button on pub rows. */
export const SendIcon: React.FC<IconProps> = ({ size = 16, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.1"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M3 11l18-8-8 18-2-8-8-2z" />
  </svg>
);

/** Chevron pointing down — disclosures. */
export const ChevronDownIcon: React.FC<IconProps> = ({ size = 15, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);

/** Chevron pointing left — carousel prev. */
export const ChevronLeftIcon: React.FC<IconProps> = ({ size = 16, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M15 6l-6 6 6 6" />
  </svg>
);

/** Chevron pointing right — link rows, carousel next. */
export const ChevronRightIcon: React.FC<IconProps> = ({ size = 16, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M9 6l6 6-6 6" />
  </svg>
);

/** X — close buttons. */
export const CloseIcon: React.FC<IconProps> = ({ size = 15, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

/** Warning triangle — warning blocks. */
export const WarningIcon: React.FC<IconProps> = ({ size = 17, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M12 9v5M12 17.5h.01" />
    <path d="M10.3 3.9L2.6 17.4A2 2 0 004.3 20.4h15.4a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z" />
  </svg>
);

/** Bookmark/flag — the Rules chip. */
export const RulesIcon: React.FC<IconProps> = ({ size = 14, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M5 4h14v16l-7-3.5L5 20z" />
  </svg>
);

/** Pencil — the editable name chip. */
export const PencilIcon: React.FC<IconProps> = ({ size = 13, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M4 20h4l10-10-4-4L4 16v4z" />
  </svg>
);

/** QR-corners glyph — the waiting pump clip on the empty screen. */
export const QrIcon: React.FC<IconProps> = ({ size = 72, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 48 48"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M6 16V8a2 2 0 012-2h8M42 16V8a2 2 0 00-2-2h-8M6 32v8a2 2 0 002 2h8M42 32v8a2 2 0 01-2 2h-8" />
    <rect x="15" y="15" width="7" height="7" rx="1" />
    <rect x="26" y="15" width="7" height="7" rx="1" />
    <rect x="15" y="26" width="7" height="7" rx="1" />
    <path d="M26 26h3M33 26v3M29 30v3M26 33h3M33 33h0" />
  </svg>
);

/** Trophy — the provisional-rank chip on the completion screen. */
export const TrophyIcon: React.FC<IconProps> = ({ size = 15, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.1"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 01-10 0z" />
    <path d="M7 6H4v1a4 4 0 004 4M17 6h3v1a4 4 0 01-4 4" />
  </svg>
);

/** Copy — copy-to-clipboard buttons. */
export const CopyIcon: React.FC<IconProps> = ({ size = 14, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <rect x="9" y="9" width="12" height="12" rx="2" />
    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
  </svg>
);
